import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { untilDestroyed } from '@ngneat/until-destroy';
import { of } from 'rxjs';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  DialogService,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  PanelComponent,
  PanelRef,
  safeDetectChanges,
  TooltipDirective,
} from '@blaze-shared/shared-ui';
import { deepClone, isDeepEqual } from '@blaze-shared/shared-util';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';

import {
  ConnectorConfigurationWithCustomFunctions,
  ConnectorForm,
  OpenAIConfiguration,
} from './openai-connector-dialog.models';
import { OpenAIConnectorFunctionsContainerComponent } from './openai-connector-functions-container/openai-connector-functions-container.component';

const DUMMY_API_KEY_PLACEHOLDER = '●●●●●●●●●●●●●●●●●●●●●●●●';
const US_DATA_RESIDENCY = 'us.api.openai.com';
const GENERAL_DATA_RESIDENCY = 'api.openai.com';

@Component({
  selector: 'blaze-openai-connector-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    EnvironmentIndicatorComponent,
    FormsModule,
    MatButtonToggleModule,
    MatIconModule,
    MatSlideToggleModule,
    OpenAIConnectorFunctionsContainerComponent,
    PanelComponent,
    TooltipDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './openai-connector-dialog.component.html',
  styleUrls: ['./openai-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'data-connector-dialog' },
})
export class OpenAIConnectorDialogComponent
  extends BaseConnectorDialogComponent<OpenAIConfiguration>
  implements OnInit, AfterViewInit
{
  private readonly dialogService = inject(DialogService);
  private panelRef = inject(PanelRef);

  private initialConfig: ConnectorConfigurationWithCustomFunctions;

  readonly serviceName = 'OpenAI';
  readonly defaultModelPlaceholder = 'gpt-4o-mini';
  connectorForm: FormGroup<ConnectorForm>;
  customFunctions: OpenAIConfiguration['customFunctions'];
  isLoading = false;
  isChildValid = signal(true);
  usDataResidency = US_DATA_RESIDENCY;
  host: FormControl<string | null>;

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      apiKey: new FormControl('', {
        nonNullable: true,
        validators: this.connectorId ? [Validators.required] : [],
      }),
      host: new FormControl<string | null>(null),
      defaultModel: new FormControl(''),
    });
    this.host = this.connectorForm.controls['host'];
    this.connectorForm.controls['apiKey'].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        const replacedValue = value?.replaceAll(DUMMY_API_KEY_PLACEHOLDER[0], '').trim() ?? '';
        if (value !== replacedValue && replacedValue) {
          this.connectorForm.controls['apiKey'].setValue(replacedValue);
        }
      });

    this.panelRef.setConfirmClose(() => {
      if (this.autoConfirmCloseDialog()) {
        return of(true);
      }

      return this.dialogService
        .openConfirmDialog({
          title: 'Unsaved changes',
          description:
            'You have unsaved changes. Are you sure you want to close the editor without saving?',
          cancelLabel: 'Cancel',
          confirmLabel: 'Yes',
        })
        .afterClosed();
    });
  }

  ngOnInit(): void {
    if (this.connector) {
      const connector = deepClone(this.connector) as ConnectorConfigurationWithCustomFunctions;
      this.fillForm(this.getConfiguration(connector));
    }
  }

  ngAfterViewInit(): void {
    if (this.connectorId) {
      this.connectorForm.controls['apiKey'].clearValidators();
      this.connectorForm.controls['apiKey'].updateValueAndValidity();
    }
    this.setupInitialConfig();
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, apiKey, defaultModel, host } = this.connectorForm.value;

    if (!connectorName) {
      return;
    }

    const connectorConfig = this.getConfigToUpdate(connectorName, apiKey, defaultModel, host);

    this.isLoading = true;
    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        this.setupInitialConfig();
        if (connector) {
          this.panelRef.close();
        }
        this.isLoading = false;
        safeDetectChanges(this.cdr);
      });
  }

  updateCustomFunctions(functions: OpenAIConfiguration['customFunctions']): void {
    this.customFunctions = functions;
  }

  getConfiguration(connector?: ConnectorConfigurationWithCustomFunctions): OpenAIConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? ({} as OpenAIConfiguration);

    const customFunctions = configuration.customFunctions ?? [];

    const apiKey = String(configuration?.apiKey ?? '');
    const defaultModel = String(configuration?.defaultModel ?? '');
    const host = configuration?.host || null;

    return {
      connectorId,
      connectorName,
      apiKey,
      defaultModel,
      customFunctions,
      host,
    };
  }

  fillForm(configuration: OpenAIConfiguration): void {
    const { connectorId, connectorName, apiKey, defaultModel, customFunctions, host } =
      configuration;

    this.connectorId = connectorId;

    this.connectorForm.patchValue(
      {
        connectorName,
        apiKey: (this.connectorId ? DUMMY_API_KEY_PLACEHOLDER : apiKey) || '',
        defaultModel,
        host,
      },
      { emitEvent: false },
    );
    this.customFunctions = customFunctions ?? [];
  }

  addCustomFunctionsToConnectorConfig(
    connector: ConnectorConfiguration,
  ): ConnectorConfigurationWithCustomFunctions {
    if (this.customFunctions) {
      // An empty array must be sent explicitly - it resets the custom functions
      // so the connector falls back to the factory ones
      (connector.configuration as any).customFunctions = this.customFunctions;
    }
    return connector as ConnectorConfigurationWithCustomFunctions;
  }

  toggleUsDataResidency(useUS: MatSlideToggleChange): void {
    this.host.setValue(useUS.checked ? US_DATA_RESIDENCY : GENERAL_DATA_RESIDENCY);
  }

  private getConfigToUpdate(
    connectorName: string,
    apiKey: string | undefined,
    defaultModel: string | null | undefined,
    host: string | null | undefined,
  ): ConnectorConfigurationWithCustomFunctions {
    apiKey = apiKey?.replaceAll(DUMMY_API_KEY_PLACEHOLDER[0], '').trim();
    return this.addCustomFunctionsToConnectorConfig(
      prepareConnectorConfiguration(
        connectorName,
        this.template?.id,
        this.accountId,
        this.initialConfig,
        {
          ...(apiKey && { apiKey }),
          defaultModel: defaultModel ?? '',
          host: host ?? GENERAL_DATA_RESIDENCY,
        },
      ),
    );
  }

  private autoConfirmCloseDialog(): boolean {
    const { connectorName, apiKey, defaultModel, host } = this.connectorForm.value;
    return isDeepEqual(
      this.initialConfig,
      this.getConfigToUpdate(connectorName || '', apiKey, defaultModel, host),
    );
  }

  private setupInitialConfig(): void {
    this.initialConfig = deepClone(
      this.getConfigToUpdate(
        this.connectorForm.value.connectorName || '',
        this.connectorForm.value.apiKey,
        this.connectorForm.value.defaultModel,
        this.connectorForm.value.host,
      ),
    );
  }
}
